import { Link } from "react-router-dom";
import { 
  Building2, 
  Calendar, 
  Users, 
  Briefcase, 
  DollarSign, 
  MapPin, 
  GraduationCap, 
  Languages, 
  Clock, 
  Tag, 
  User 
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SkillBadge } from "@/components/SkillBadge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getFormattedDateTime } from "@/utils/settings";
import { useSettings } from "@/providers/SettingsProvider";
import { useTranslation } from "react-i18next";

interface JobOverviewCardProps {
  company: {
    id: string;
    name: string;
  };
  formattedDate: string;
  matchesCount: number;
  skills: string[];
  
  // Position details
  seniorityLevel?: string;
  employmentType?: string;
  contractDuration?: string;
  employmentClassification?: string;
  
  // Timeline
  startDate?: string;
  applicationDeadline?: string;
  jobPostingDate?: string;
  
  // Compensation
  budget?: string;
  budgetFreelance?: string;
  
  // Location
  locationType?: string;
  workplaceBreakdown?: string;
  
  // Skills
  hardSkills?: string[];
  softSkills?: string[];
  
  // Language
  languageRequirements?: string | string[];
  extraLang?: string;
  
  // Additional
  educationLevel?: string;
  yearsOfExperience?: string;
  cultureTags?: string[];
  source?: string;
  assignedRecruiter?: string;
  
  // Layout option
  horizontal?: boolean;
}

export const JobOverviewCard = ({ 
  company, 
  formattedDate, 
  matchesCount, 
  skills,
  seniorityLevel,
  employmentType,
  contractDuration,
  employmentClassification,
  startDate,
  applicationDeadline,
  jobPostingDate,
  budget,
  budgetFreelance,
  locationType,
  workplaceBreakdown,
  hardSkills,
  softSkills,
  languageRequirements,
  extraLang,
  educationLevel,
  yearsOfExperience,
  cultureTags,
  source,
  assignedRecruiter,
  horizontal = false
}: JobOverviewCardProps) => {
  const { dateFormat, timeFormat } = useSettings();
  const { t } = useTranslation();
  
  // Helper function to check if a section has data to display
  const hasData = (values: (string | string[] | undefined)[]) => {
    return values.some(value => {
      if (Array.isArray(value)) return value && value.length > 0;
      return !!value;
    });
  };

  // Format dates according to user settings
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return getFormattedDateTime(new Date(dateString), dateFormat, timeFormat, false);
  };

  // Helper to normalize language requirements to an array
  const normalizeLanguageRequirements = (langReq: string | string[] | null | undefined): string[] => {
    
    if (!langReq) return [];

    if (Array.isArray(langReq)) return langReq;

    // First split by commas, then clean up each item
    return langReq.split(',').map(l => l.replace(/[^a-zA-Z0-9]/g, '')).filter(Boolean);

  };

  // Helper to translate a language name
  const translateLanguageName = (language: string): string => {
    // Convert to lowercase and remove trailing/leading whitespace for consistent comparison
    const lowercase = language.toLowerCase().trim();
    
    // List of supported languages in our i18n
    const supportedLanguages = ['english', 'dutch', 'french', 'german', 'spanish', 'italian'];
    
    // If it's a supported language, translate it
    if (supportedLanguages.includes(lowercase)) {
      return t(`jobs.formWizard.languages.${lowercase}`);
    }
    
    // For 'other' or any custom language, just return as is
    return language;
  };

  // Helper to translate education level
  const translateEducationLevel = (level?: string): string => {
    if (!level) return '';
    
    // Map from API values to i18n keys
    const educationLevelMap: Record<string, string> = {
      'HIGH_SCHOOL': 'highSchool',
      'ASSOCIATE': 'associate',
      'BACHELOR': 'bachelor',
      'MASTER': 'master',
      'DOCTORATE': 'doctorate',
      'NOT_REQUIRED': 'notRequired'
    };
    
    // Get the corresponding i18n key or use the original value if not found
    const i18nKey = educationLevelMap[level] || level.toLowerCase();
    
    // Translate the education level
    return t(`jobs.formWizard.educationLevels.${i18nKey}`);
  };

  // Get normalized language requirements
  const normalizedLanguages = normalizeLanguageRequirements(languageRequirements);
  const translatedLanguages = normalizedLanguages.map(lang => translateLanguageName(lang));

  console.log(translatedLanguages, normalizedLanguages, languageRequirements, "LANGUAGES");

  const hasPositionDetails = hasData([seniorityLevel, employmentType, contractDuration, employmentClassification]);
  const hasTimeline = hasData([startDate, applicationDeadline, jobPostingDate]);
  const hasCompensation = hasData([budget, budgetFreelance]);
  const hasLocation = hasData([locationType, workplaceBreakdown]);
  const hasSkills = hasData([skills, hardSkills, softSkills]);
  const hasLanguage = hasData([languageRequirements, extraLang]);
  const hasAdditional = hasData([educationLevel, yearsOfExperience, cultureTags, source, assignedRecruiter]);

  // If in horizontal mode, render a simplified card with multiple columns
  if (horizontal) {
    return (
      <Card className="bg-white shadow mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{t("jobs.formWizard.title", "Job Overview")}</CardTitle>
        </CardHeader>
        
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Column 1: Basic Info */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium">{t("jobs.formWizard.basicInfo", "Basic Info")}</h3>
              
              <div className="flex items-center">
                <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                <Link to={`/companies/${company.id}`} className="text-digger-blue hover:underline text-sm">
                  {company.name}
                </Link>
              </div>
              
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm">{formattedDate}</span>
              </div>
              
              <div className="flex items-center">
                <Users className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm">{matchesCount} {t("jobs.formWizard.candidates", "candidates")}</span>
              </div>
              
              {hasPositionDetails && (
                <>
                  {seniorityLevel && (
                    <div className="flex items-center">
                      <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-xs text-gray-500">{t("jobs.formWizard.seniorityLevel", "Seniority:")}</span>
                      <span className="text-sm ml-1">{seniorityLevel}</span>
                    </div>
                  )}
                  
                  {employmentType && (
                    <div className="flex items-center">
                      <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-xs text-gray-500">{t("jobs.formWizard.employmentType", "Type:")}</span>
                      <span className="text-sm ml-1">{employmentType}</span>
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Column 2: Timeline and Compensation */}
            <div className="space-y-2">
              {hasTimeline && (
                <>
                  <h3 className="text-sm font-medium">{t("jobs.formWizard.timeline", "Timeline")}</h3>
                  
                  {startDate && (
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-xs text-gray-500">{t("jobs.formWizard.start", "Start:")}</span>
                      <span className="text-sm ml-1">{formatDate(startDate)}</span>
                    </div>
                  )}
                  
                  {applicationDeadline && (
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-xs text-gray-500">{t("jobs.formWizard.deadline", "Deadline:")}</span>
                      <span className="text-sm ml-1">{formatDate(applicationDeadline)}</span>
                    </div>
                  )}
                </>
              )}
              
              {hasCompensation && (
                <>
                  <h3 className="text-sm font-medium mt-3">{t("jobs.formWizard.compensation", "Compensation")}</h3>
                  
                  {budget && (
                    <div className="flex items-center">
                      <DollarSign className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm">{budget}</span>
                    </div>
                  )}
                  
                  {budgetFreelance && (
                    <div className="flex items-center">
                      <DollarSign className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-xs text-gray-500">{t("jobs.formWizard.freelance", "Freelance:")}</span>
                      <span className="text-sm ml-1">{budgetFreelance}</span>
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Column 3: Location and Language */}
            <div className="space-y-2">
              {hasLocation && (
                <>
                  <h3 className="text-sm font-medium">{t("jobs.formWizard.location", "Location")}</h3>
                  
                  {locationType && (
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm">{locationType}</span>
                    </div>
                  )}
                  
                  {workplaceBreakdown && locationType === "Hybrid" && (
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-xs text-gray-500">{t("jobs.formWizard.breakdown", "Breakdown:")}</span>
                      <span className="text-sm ml-1">{workplaceBreakdown}</span>
                    </div>
                  )}
                </>
              )}
              
              {hasLanguage && (
                <>
                  <h3 className="text-sm font-medium mt-3">{t("jobs.formWizard.languageRequirements", "Language Requirements")}</h3>
                  
                  {normalizedLanguages.length > 0 && (
                    <div className="flex items-center">
                      <Languages className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm">{translatedLanguages.join(', ')}</span>
                    </div>
                  )}
                  
                  {extraLang && (
                    <div className="flex items-center">
                      <Languages className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-xs text-gray-500">{t("jobs.formWizard.additional", "Additional:")}</span>
                      <span className="text-sm ml-1">{extraLang}</span>
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Column 4: Experience and Additional */}
            <div className="space-y-2">
              {hasAdditional && (
                <>
                  <h3 className="text-sm font-medium">{t("jobs.formWizard.experience", "Experience")}</h3>
                  
                  {educationLevel && (
                    <div className="flex items-center">
                      <GraduationCap className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm">{translateEducationLevel(educationLevel)}</span>
                    </div>
                  )}
                  
                  {yearsOfExperience && (
                    <div className="flex items-center">
                      <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm">{yearsOfExperience}</span>
                    </div>
                  )}
                  
                  {source && (
                    <div className="flex items-center">
                      <Tag className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-xs text-gray-500">{t("jobs.formWizard.source", "Source:")}</span>
                      <span className="text-sm ml-1">{source}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          
          {/* Skills Section at the bottom */}
          {hasSkills && (
            <div className="mt-4 pt-4 border-t">
              <h3 className="text-sm font-medium mb-2">{t("jobs.formWizard.keySkills", "Skills & Expertise")}</h3>
              
              <div className="flex flex-wrap gap-2">
                {skills && skills.map((skill, index) => (
                  <SkillBadge key={index} label={skill} size="sm" />
                ))}
                
                {hardSkills && hardSkills.map((skill, index) => (
                  <SkillBadge key={`hard-${index}`} label={skill} size="sm" />
                ))}
                
                {softSkills && softSkills.map((skill, index) => (
                  <SkillBadge key={`soft-${index}`} label={skill} size="sm" />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }
  
  // Original vertical layout
  return (
    <Card className="bg-white shadow mb-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">{t("jobs.formWizard.title", "Job Overview")}</CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Company, Posted date and Matches */}
        <div className="space-y-2 pb-3 border-b">
          <div className="flex items-center">
            <Building2 className="h-4 w-4 text-gray-400 mr-2" />
            <Link to={`/companies/${company.id}`} className="text-digger-blue hover:underline text-sm">
              {company.name}
            </Link>
          </div>
          
          <div className="flex items-center">
            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
            <span className="text-sm">{formattedDate}</span>
          </div>
          
          <div className="flex items-center">
            <Users className="h-4 w-4 text-gray-400 mr-2" />
            <span className="text-sm">{matchesCount} {t("jobs.formWizard.candidates", "candidates")}</span>
          </div>
        </div>
        
        {/* Position Details */}
        {hasPositionDetails && (
          <div className="space-y-2 pb-3 border-b">
            <h3 className="text-sm font-medium">{t("jobs.formWizard.positionDetails", "Position Details")}</h3>
            
            {seniorityLevel && (
              <div className="flex items-center">
                <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.seniorityLevel", "Seniority Level")}</span>
                <span className="text-sm">{seniorityLevel}</span>
              </div>
            )}
            
            {employmentType && (
              <div className="flex items-center">
                <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.employmentType", "Employment Type")}</span>
                <span className="text-sm">{employmentType}</span>
              </div>
            )}
            
            {contractDuration && (
              <div className="flex items-center">
                <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.contractDuration", "Contract Duration")}</span>
                <span className="text-sm">{contractDuration}</span>
              </div>
            )}
            
            {employmentClassification && (
              <div className="flex items-center">
                <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.timeCommitment", "Time Commitment")}</span>
                <span className="text-sm">{employmentClassification}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Timeline */}
        {hasTimeline && (
          <div className="space-y-2 pb-3 border-b">
            <h3 className="text-sm font-medium">{t("jobs.formWizard.timeline", "Timeline")}</h3>
            
            {startDate && (
              <div className="flex items-center">
                <Clock className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.startDate", "Start Date")}</span>
                <span className="text-sm">{formatDate(startDate)}</span>
              </div>
            )}
            
            {applicationDeadline && (
              <div className="flex items-center">
                <Clock className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.applicationDeadline", "Application Deadline")}</span>
                <span className="text-sm">{formatDate(applicationDeadline)}</span>
              </div>
            )}
            
            {jobPostingDate && (
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.jobPostedOn", "Job Posted On")}</span>
                <span className="text-sm">{formatDate(jobPostingDate)}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Compensation */}
        {hasCompensation && (
          <div className="space-y-2 pb-3 border-b">
            <h3 className="text-sm font-medium">{t("jobs.formWizard.compensation", "Compensation")}</h3>
            
            {budget && (
              <div className="flex items-center">
                <DollarSign className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.budgetPermanent", "Budget (Permanent)")}</span>
                <span className="text-sm">{budget}</span>
              </div>
            )}
            
            {budgetFreelance && (
              <div className="flex items-center">
                <DollarSign className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.budgetFreelance", "Budget (Freelance)")}</span>
                <span className="text-sm">{budgetFreelance}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Location */}
        {hasLocation && (
          <div className="space-y-2 pb-3 border-b">
            <h3 className="text-sm font-medium">{t("jobs.formWizard.location", "Location")}</h3>
            
            {locationType && (
              <div className="flex items-center">
                <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.locationType", "Location Type")}</span>
                <span className="text-sm">{locationType}</span>
              </div>
            )}
            
            {workplaceBreakdown && locationType === "Hybrid" && (
              <div className="flex items-center">
                <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.breakdown", "Breakdown")}</span>
                <span className="text-sm">{workplaceBreakdown}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Skills */}
        {hasSkills && (
          <div className="space-y-2 pb-3 border-b">
            <h3 className="text-sm font-medium">{t("jobs.formWizard.keySkills", "Skills & Expertise")}</h3>
            
            {skills && skills.length > 0 && (
              <div className="mb-2">
                <span className="text-xs text-gray-500 block mb-1">{t("jobs.formWizard.skills", "Skills")}</span>
                <div className="flex flex-wrap gap-1">
                  {skills.map((skill, index) => (
                    <SkillBadge key={index} label={skill} size="sm" />
                  ))}
                </div>
              </div>
            )}
            
            {hardSkills && hardSkills.length > 0 && (
              <div className="mb-2">
                <span className="text-xs text-gray-500 block mb-1">{t("jobs.formWizard.hardSkills", "Hard Skills")}</span>
                <div className="flex flex-wrap gap-1">
                  {hardSkills.map((skill, index) => (
                    <SkillBadge key={index} label={skill} size="sm" />
                  ))}
                </div>
              </div>
            )}
            
            {softSkills && softSkills.length > 0 && (
              <div>
                <span className="text-xs text-gray-500 block mb-1">{t("jobs.formWizard.softSkills", "Soft Skills")}</span>
                <div className="flex flex-wrap gap-1">
                  {softSkills.map((skill, index) => (
                    <SkillBadge key={index} label={skill} size="sm" />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Language Requirements */}
        {hasLanguage && (
          <div className="space-y-2 pb-3 border-b">
            <h3 className="text-sm font-medium">{t("jobs.formWizard.languageRequirements", "Language Requirements")}</h3>
            
            {normalizedLanguages.length > 0 && (
              <div className="flex items-center">
                <Languages className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.languages", "Languages")}</span>
                <span className="text-sm">{translatedLanguages.join(', ')}</span>
              </div>
            )}
            
            {extraLang && (
              <div className="flex items-center">
                <Languages className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.additional", "Additional")}</span>
                <span className="text-sm">{extraLang}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Additional Requirements */}
        {hasAdditional && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">{t("jobs.formWizard.additionalRequirements", "Additional Requirements")}</h3>
            
            {educationLevel && (
              <div className="flex items-center">
                <GraduationCap className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.educationLevel", "Education Level")}</span>
                <span className="text-sm">{translateEducationLevel(educationLevel)}</span>
              </div>
            )}
            
            {yearsOfExperience && (
              <div className="flex items-center">
                <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.experienceRequired", "Experience Required")}</span>
                <span className="text-sm">{yearsOfExperience}</span>
              </div>
            )}
            
            {cultureTags && cultureTags.length > 0 && (
              <div>
                <span className="text-xs text-gray-500 block mb-1">{t("jobs.formWizard.cultureTags", "Culture Tags")}</span>
                <div className="flex flex-wrap gap-1">
                  {cultureTags.map((tag, index) => (
                    <span key={index} className="bg-gray-100 px-2 py-0.5 rounded-md text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {source && (
              <div className="flex items-center">
                <Tag className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.source", "Source")}</span>
                <span className="text-sm">{source}</span>
              </div>
            )}
            
            {assignedRecruiter && (
              <div className="flex items-center">
                <User className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 w-24">{t("jobs.formWizard.assignedRecruiter", "Assigned Recruiter")}</span>
                <span className="text-sm">{assignedRecruiter}</span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
